import axios from 'axios';
import { useAuthStore } from '../store/authStore';

// Use relative paths so the browser uses whatever protocol the page uses (HTTPS)
// Nginx reverse-proxies /api/ to the backend
const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors with automatic token refresh
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for token to be refreshed
        return new Promise((resolve) => {
          addRefreshSubscriber((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }
      
      isRefreshing = true;
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          { baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } }
        );
        
        const { access_token, refresh_token, expires_in } = response.data;
        
        // Update tokens in localStorage and authStore
        localStorage.setItem('token', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        localStorage.setItem('tokenExpiresIn', expires_in);
        
        const authStore = useAuthStore.getState();
        authStore.setToken(access_token, refresh_token, expires_in);
        
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        isRefreshing = false;
        onRefreshed(access_token);
        
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        // Refresh token is invalid, logout user
        const authStore = useAuthStore.getState();
        authStore.logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    if (error.response?.status === 403) {
      // Handle access denied - user doesn't have permission for this module
      const message = error.response?.data?.detail || 'You do not have permission to access this resource';
      console.warn('Access Denied:', message);
    }
    return Promise.reject(error);
  }
);

export const apiClient = api;

// Auth API
export const authAPI = {
  login: (username, password) => 
    api.post('/auth/login', { username, password }),
  register: (data) => 
    api.post('/auth/register', data),
  getCurrentUser: () => 
    api.get('/auth/me'),
  refreshToken: (refreshToken) =>
    api.post('/auth/refresh', { refresh_token: refreshToken }),
  requestPasswordReset: (data) =>
    api.post('/auth/password-reset-request', data),
  resetPassword: (data) =>
    api.post('/auth/password-reset-verify', data),
  changePassword: (data) =>
    api.post('/auth/change-password', data),
};

// Companies API
export const companiesAPI = {
  getAll: () => api.get('/companies/'),
  getPublic: () => api.get('/companies/public/all'),
  getById: (id) => api.get(`/companies/${id}/`),
  create: (data) => api.post('/companies/', data),
  update: (id, data) => api.put(`/companies/${id}/`, data),
  delete: (id) => api.delete(`/companies/${id}/`),
};

// Devices API
export const devicesAPI = {
  // Accept optional query params (company_id, upload_batch_id, etc.)
  getAll: (params) => api.get('/devices/', { params }),
  getPublic: () => api.get('/devices/public/all'),
  getById: (id) => api.get(`/devices/${id}/`),
  create: (data) => api.post('/devices/', data),
  update: (id, data) => api.put(`/devices/${id}/`, data),
  delete: (id) => api.delete(`/devices/${id}/`),
  bulkUpload: (formData, onUploadProgress) => {
    const config = {
      onUploadProgress,
    };
    
    // Create a fresh request without the default JSON content-type
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // Do NOT set Content-Type - let axios/browser handle it for multipart
    
    return axios.post(
      `${API_BASE_URL}/devices/bulk-upload`,
      formData,
      { 
        ...config,
        headers
      }
    );
  },
  getBatches: (params) => api.get('/devices/batches', { params }),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users/'),
  getById: (id) => api.get(`/users/${id}/`),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}/`, data),
  delete: (id) => api.delete(`/users/${id}/`),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { new_password: newPassword }),
};

// Depots API
export const depotsAPI = {
  getAll: () => api.get('/depots/'),
  getPublic: () => api.get('/depots/public/all'),
  getById: (id) => api.get(`/depots/${id}/`),
  create: (data) => api.post('/depots/', data),
  update: (id, data) => api.put(`/depots/${id}/`, data),
  delete: (id) => api.delete(`/depots/${id}/`),
};

// Notifications API
export const notificationsAPI = {
  getAll: (filters) => api.get('/notifications/', { params: filters }),
  getById: (id) => api.get(`/notifications/${id}/`),
  create: (data) => api.post('/notifications/', data),
  update: (id, data) => api.put(`/notifications/${id}/`, data),
  delete: (id) => api.delete(`/notifications/${id}/`),
};

// Device Assignments API
export const boxAssignmentsAPI = {
  getAll: () => api.get('/device-assignments/'),
  getById: (id) => api.get(`/device-assignments/${id}/`),
  create: (data) => api.post('/device-assignments/', data),
  bulkCreate: (data) => api.post('/device-assignments/bulk', data),
  update: (id, data) => api.put(`/device-assignments/${id}/`, data),
  delete: (id) => api.delete(`/device-assignments/${id}/`),
};

// Container Assignment API (Box Assignment)
export const containerAssignmentAPI = {
  getAll: () => api.get('/container-assignment/'),
  getById: (id) => api.get(`/container-assignment/${id}/`),
  getByBag: (bag) => api.get(`/container-assignment/by-bag/${bag}`),
  create: (data) => api.post('/container-assignment/', data),
  update: (id, data) => api.put(`/container-assignment/${id}/`, data),
  delete: (id) => api.delete(`/container-assignment/${id}/`),
  bulkAssign: (formData) => api.post('/container-assignment/bulk-assign', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Audit Logs API
export const auditLogsAPI = {
  getAll: (filters) => api.get('/audit-logs/', { params: filters }),
  getById: (id) => api.get(`/audit-logs/${id}/`),
};

// Reports API
export const reportsAPI = {
  getSummary: (startDate, endDate) => 
    api.get('/reports/summary', { params: { start_date: startDate, end_date: endDate } }),
  getDevicesReport: (companyId) => 
    api.get('/reports/devices', { params: { company_id: companyId } }),
  getNotificationsReport: (companyId) => 
    api.get('/reports/notifications', { params: { company_id: companyId } }),
  getSealsAssignedData: (filters) =>
    api.get('/reports/seals-assigned/data', { params: filters }),
  // New detailed reports
  getSealsLoaded: (filters) =>
    api.get('/reports/seals-loaded', { params: filters }),
  getSealsTracing: (filters) =>
    api.get('/reports/seals-tracing', { params: filters }),
  getGlobalInventory: (filters) =>
    api.get('/reports/global-inventory', { params: filters }),
  getClientSealsReceived: (filters) =>
    api.get('/reports/client/seals-received', { params: filters }),
  getClientSealsAssignedDepots: (filters) =>
    api.get('/reports/client/seals-assigned-depots', { params: filters }),
  getSealsUsed: (filters) =>
    api.get('/reports/seals-used', { params: filters }),
  getDistributorBoxesAssigned: (filters) =>
    api.get('/reports/distributor/boxes-assigned', { params: filters }),
  getDistributorInventoryStatus: (filters) =>
    api.get('/reports/distributor/inventory-status', { params: filters }),
  exportSealsAssignedCSV: (filters) =>
    api.get('/reports/seals-assigned/export/csv', { 
      params: filters,
      responseType: 'blob'
    }),
  exportSealsAssignedExcel: (filters) =>
    api.get('/reports/seals-assigned/export/excel', { 
      params: filters,
      responseType: 'blob'
    }),
  exportSealsLoadedExcel: (filters) =>
    api.get('/reports/seals-loaded/export/excel', {
      params: filters,
      responseType: 'blob',
    }),
  exportSealsTracingExcel: (filters) =>
    api.get('/reports/seals-tracing/export/excel', {
      params: filters,
      responseType: 'blob',
    }),
  exportGlobalInventoryExcel: (filters) =>
    api.get('/reports/global-inventory/export/excel', {
      params: filters,
      responseType: 'blob',
    }),
  exportClientSealsReceivedExcel: (filters) =>
    api.get('/reports/client/seals-received/export/excel', {
      params: filters,
      responseType: 'blob',
    }),
  exportClientSealsAssignedDepotsExcel: (filters) =>
    api.get('/reports/client/seals-assigned-depots/export/excel', {
      params: filters,
      responseType: 'blob',
    }),
  exportSealsUsedExcel: (filters) =>
    api.get('/reports/seals-used/export/excel', {
      params: filters,
      responseType: 'blob',
    }),
  exportDistributorBoxesAssignedExcel: (filters) =>
    api.get('/reports/distributor/boxes-assigned/export/excel', {
      params: filters,
      responseType: 'blob',
    }),
  exportDistributorInventoryStatusExcel: (filters) =>
    api.get('/reports/distributor/inventory-status/export/excel', {
      params: filters,
      responseType: 'blob',
    }),
  // CSV exports for all detailed reports
  exportSealsLoadedCSV: (filters) =>
    api.get('/reports/seals-loaded/export/csv', {
      params: filters,
      responseType: 'blob',
    }),
  exportSealsTracingCSV: (filters) =>
    api.get('/reports/seals-tracing/export/csv', {
      params: filters,
      responseType: 'blob',
    }),
  exportGlobalInventoryCSV: (filters) =>
    api.get('/reports/global-inventory/export/csv', { params: filters, responseType: 'blob' }),
  exportClientSealsReceivedCSV: (filters) =>
    api.get('/reports/client/seals-received/export/csv', {
      params: filters,
      responseType: 'blob',
    }),
  exportClientSealsAssignedDepotsCSV: (filters) =>
    api.get('/reports/client/seals-assigned-depots/export/csv', {
      params: filters,
      responseType: 'blob',
    }),
  exportSealsUsedCSV: (filters) =>
    api.get('/reports/seals-used/export/csv', {
      params: filters,
      responseType: 'blob',
    }),
  exportDistributorBoxesAssignedCSV: (filters) =>
    api.get('/reports/distributor/boxes-assigned/export/csv', {
      params: filters,
      responseType: 'blob',
    }),
  exportDistributorInventoryStatusCSV: (filters) =>
    api.get('/reports/distributor/inventory-status/export/csv', {
      params: filters,
      responseType: 'blob',
    }),
};
